import React from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  TopToolbar,
  CreateButton,
  sanitizeListRestProps,
  Record,
  FunctionField,
  Responsive,
  SimpleList,
} from "react-admin";
import "../../common/styles/list-style.css";
import { IFleetManagerGridView } from "../../../common/data/data-types/fleet-managers";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { Box, useTheme, Theme, Tooltip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useListContext } from "react-admin";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import EmailIcon from "@material-ui/icons/Email";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";

export const MasterFleetManagersList = (props: IResourceListProps) => {
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const permissions = useActionPermissions();

  const ListFilter = (props: any) => {
    const { loading } = useListContext();
    useSearchFields(props, ["fullName", "email"]);
    return (
      <Filter {...props}>
        {!loading && (
          <TextInput
            className="fleet-managers-list-filter search-box"
            label="resources.common.labels.search"
            source="q"
            alwaysOn
            autoFocus
            autoComplete="off"
          />
        )}
        {loading && (
          <TextInput
            className="fleet-managers-list-filter search-box"
            label="resources.common.labels.search"
            source="q"
            alwaysOn
          />
        )}
      </Filter>
    );
  };

  const ListActions = (props: any) => {
    const { className, exporter, filters, maxResults, ...rest } = props;
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
        {permissions &&
          permissions[permissionNames.admin.masterFleetManagers.main] &&
          permissions[permissionNames.admin.masterFleetManagers.create] && (
            <CreateButton basePath={basePath} />
          )}
      </TopToolbar>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          source="fullName"
          label="resources.admin-fleet-managers.labels.fullName"
        />

        <FunctionField<IFleetManagerGridView & Record>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          marginTop: "14px",
        }}
      >
        <Tooltip title="E-mail">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <EmailIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <TextField source="email" label="resources.common.labels.email" />
          </div>
        </Tooltip>
        <Tooltip title="Role">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <AssignmentIndIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <TextField
              source="roleName"
              label="resources.admin-fleet-managers.labels.role"
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="content-container">
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={SupervisorAccountOutlinedIcon} />}
        className="content-list"
        title="resources.master-fleet-managers.name"
        bulkActionButtons={false}
        actions={<ListActions />}
        filters={<ListFilter />}
        sort={{ field: "fullName", order: "ASC" }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              linkType="edit"
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              secondaryText={(record) => (
                <RenderSecondaryTextForSimpleList record={record} />
              )}
              rightIcon={(record) => {
                return (
                  <ActionsField
                    hasShow={props.hasShow}
                    {...props}
                    record={record}
                    editable={() =>
                      permissions &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.main
                      ] &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.update
                      ]
                    }
                    canActivateOrDeactivate={() =>
                      permissions &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.main
                      ] &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.update
                      ]
                    }
                  >
                    {permissions &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.main
                      ] &&
                      permissions[
                        permissionNames.admin.masterFleetManagers.delete
                      ] && <DeleteRecordMenuItem />}
                  </ActionsField>
                );
              }}
            />
          }
          medium={
            <Datagrid>
              <TextField
                source="fullName"
                label="resources.admin-fleet-managers.labels.fullName"
              />
              <TextField source="email" label="resources.common.labels.email" />
              <TextField
                source="roleName"
                label="resources.admin-fleet-managers.labels.role"
              />
              <FunctionField<IFleetManagerGridView & Record>
                source="status"
                headerClassName="centered-content"
                label="resources.common.labels.active"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.status === "Active" ? (
                      <CheckBoxIcon
                        className={record.status}
                        style={{
                          color:
                            themeName === "dark"
                              ? theme.palette.success.dark
                              : theme.palette.success.light,
                        }}
                      />
                    ) : (
                      <IndeterminateCheckBoxIcon
                        className={record?.status}
                        style={{
                          color:
                            themeName === "dark"
                              ? theme.palette.error.dark
                              : theme.palette.error.light,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <ActionsField
                hasShow={props.hasShow}
                editable={() =>
                  permissions &&
                  permissions[permissionNames.admin.masterFleetManagers.main] &&
                  permissions[permissionNames.admin.masterFleetManagers.update]
                }
                canActivateOrDeactivate={() =>
                  permissions &&
                  permissions[permissionNames.admin.masterFleetManagers.main] &&
                  permissions[permissionNames.admin.masterFleetManagers.update]
                }
              >
                {permissions &&
                  permissions[permissionNames.admin.masterFleetManagers.main] &&
                  permissions[
                    permissionNames.admin.masterFleetManagers.delete
                  ] && <DeleteRecordMenuItem />}
              </ActionsField>
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
